// @flow

import React, { useState, useEffect, useRef } from "react";
import { localize } from "redux-i18n";
import classNames from "classnames";
import moment from "moment";

import { schemaFnc } from "./schema";
import uiSchema from "./ui-schema";
import type { SelectedTask } from "@modules/reporting/reducer/tasks/types";
import type {
  AbsenceMonitoringTaskType,
  Level,
} from "@modules/service/reducer/absence-monitoring/types.js";
import { Button, SchemaForm, View } from "@components";
import config from "@config";

import styles from "./absence-review.scss";
import Ajv from "ajv";
import { removeNullAndUndefinedProps } from "@transformers/object";

type OwnProps = $Exact<{
  onRef: (ref: *) => void,
  task: AbsenceMonitoringTaskType,
  externalSubmit: ?boolean,
  disabled: boolean,
  isModal?: boolean,
  onCancel: () => void,
  onSubmit: (formData: AbsenceMonitoringTaskType) => void,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
}>;

export function AbsenceReviewForm({ t, task, disabled, onSubmit, isModal, onCancel }: Props) {
  const formRef = useRef(null);
  const [data, setData]: [?AbsenceMonitoringTaskType, Function] = useState(task);
  const [schema, setSchema] = useState(prepareSchema());

  useEffect(() => {
    if (!task || !task.levels.length) return;
    const newData = {
      ...task,
      currentLevel: task.levels.find(l => l.id === (task.newLevel ? task.newLevel.id : null)),
      action: task.action || "LOG_ACTION", // set default selected option
    };

    setData(newData);
  }, [task]);

  function isFormDataValid() {
    if (!data) return false;
    let noActionReasons = !!data.noActionReasons.length;
    let additionalSupportActions = !!data.additionalSupportActions.length;
    let levels = !!data.levels.length;

    return noActionReasons && additionalSupportActions && levels;
  }

  function prepareSchema() {
    let jsonSchema = null;
    if (data) {
      const currentLevel = data.levels.find(
        (l: Level) => l.id === (data.newLevel ? data.newLevel.id : null),
      );
      const reviewPeriodLength = currentLevel ? currentLevel.reviewPeriodLength : 6;
      jsonSchema = schemaFnc({
        task: data,
        readonly: disabled,
        reviewPeriodLength: reviewPeriodLength,
        t,
      });
    }
    return jsonSchema;
  }

  function isValid() {
    if (data && schema && isFormDataValid()) {
      const validate = new Ajv().compile(schema);
      const res = validate(removeNullAndUndefinedProps(data));
      if (!res) console.warn("The form is not valid", validate.errors);
      return res;
    }

    return false;
  }

  function handleChange(formData: Object) {
    setData(formData);
  }

  function handleSubmit(formData) {
    if (!data) return;
    const { action } = data;
    const tmpData = { ...data };
    if (action === "LOG_ACTION") {
      delete tmpData.noActionReason;
    } else if (action === "NO_ACTION") {
      delete tmpData.additionalSupportActionsTaken;
      delete tmpData.additionalSupportActions;
      delete tmpData.meetingDate;
      delete tmpData.newLevel;
      delete tmpData.nextReviewDate;
    }

    isValid() && onSubmit(tmpData);
  }

  function submitSchemaForm() {
    return formRef.current.submit();
  }

  return (
    <div>
      {data && (
        <div
          className={classNames("absence-review__container", {
            "absence-review__container--disabled": disabled,
          })}
        >
          {isFormDataValid() ? (
            <SchemaForm
              ref={formRef}
              className="w-100"
              readonly={disabled}
              disabled={disabled}
              formData={data}
              schema={schema}
              uiSchema={uiSchema({
                monitoringType: data.absenceMonitoringType,
                readonly: disabled,
                t: t,
                supportActionsTaken: !!(data.additionalSupportActionsTaken || []).length,
              })}
              onSubmit={handleSubmit}
              onChange={handleChange}
              externalSubmit
            />
          ) : (
            <div className="pt-4 pb-4 text-align-center">
              <h6>{t("TASKS_INSUFFICIENT_DATA")}</h6>
            </div>
          )}
        </div>
      )}
      {!isModal && onSubmit && (
        <div className="d-flex justify-content-end my-5">
          <View>
            <Button
              materialIcon="replay_circle_filled"
              onClick={submitSchemaForm}
              disabled={!isValid() || !isFormDataValid()}
            >
              {t("SUBMIT")}
            </Button>
          </View>
        </div>
      )}
      {isModal && onSubmit && (
        <View direction="row" justifyContent="flex-end" gap={2} margin="1rem 0 0">
          <Button color="secondary" onClick={onCancel}>
            {t("CANCEL")}
          </Button>
          <Button
            materialIcon="replay_circle_filled"
            onClick={submitSchemaForm}
            disabled={!isValid() || !isFormDataValid()}
          >
            {t("SUBMIT")}
          </Button>
        </View>
      )}
    </div>
  );
}

export default localize()(AbsenceReviewForm);
